<template>
    <Modal
        animation-name="up"
        class="discount-modal"
        :is-open="isOpen"
        aria-modal="true"
        role="dialog"
        @modal-closed="handleCloseModal"
    >
        <template #content>
            <div class="modal-dialog" ref="trapRef">
                <div class="modal-header">
                    <button type="button" class="close" @click="handleCloseModal">×</button>
                    <h4 id="discount-modal-title" class="modal-title">{{ blok.modalTitle }}</h4>
                </div>

                <form class="modal-form" novalidate @submit.prevent="submitForm">
                    <div class="main-form__wrapper">
                        <div class="input-group">
                            <div class="input-wrapper">
                                <FormInput
                                    v-model="formInput.email"
                                    :placeholder="blok.inputPlaceholder || t('placeholders.email3')"
                                    name="email"
                                    type="email"
                                    :aria-label="blok.modalTitle ? undefined : 'Email address'"
                                    :aria-labelledby="blok.modalTitle ? 'discount-modal-title' : undefined"
                                    @input="onEmailInput"
                                ></FormInput>

                                <div
                                    v-if="displayMessage"
                                    class="error-message"
                                    :class="{ 'suggestion-message': displayMessage.type === 'suggestion' }"
                                >
                                    <span
                                        v-if="displayMessage.type === 'suggestion'"
                                        class="underline cursor-pointer"
                                        @click.stop="applyTypoSuggestion"
                                    >
                                        {{ displayMessage.text }}
                                    </span>
                                    <span v-else>
                                        {{ displayMessage.text }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div class="button-group">
                            <button type="submit" name="Submit" :disabled="isLoading || shouldDisableButton">
                                {{ blok.submitButton || t('buttons.applyDiscount') }}
                            </button>
                        </div>
                    </div>
                    <div class="modal-text" v-html="renderRichText(blok.modalText)"></div>
                </form>
                <div class="modal-footer">
                    <button type="button" class="close" :disabled="isLoading" @click.prevent="handleCloseModal">
                        {{ blok.cancelButton || t('buttons.cancelDiscount') }}
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import Modal from './Modal.vue';
import FormInput from '@nuxt-monorepo/ui/components/Form/Input.vue';
import { useModalPrompt } from '@nuxt-monorepo/ui/composables/useModalPrompt';
import type { IDiscountModal } from '@nuxt-monorepo/ui/types/components/modals/DiscountModal';
import { useModalStore } from '@nuxt-monorepo/base/app/store/modal';
import { useFocusTrap } from '@nuxt-monorepo/ui/composables/useFocusTrap';
import { useEmailFieldValidation } from '@nuxt-monorepo/ui/composables/useEmailFieldValidation';
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';

const globalStore = useGlobalStore();

const { blok } = defineProps<{ blok: IDiscountModal }>();

const { locale, locales } = useI18n();
const localeObj = Object.values(locales.value).find((item: any) => item.code === locale.value);

const formInput = ref<{ email: string }>({
    email: '',
});

const { trapRef } = useFocusTrap();
const isLoading = ref(false);

const { t, te } = useI18n();

const email = computed<string>({
    get: () => formInput.value.email,
    set: (value: string) => {
        formInput.value.email = value;
    },
});

const {
    v$,
    emailError,
    suggestedEmail,
    roleNudgeMessage,
    shouldDisableButton,
    onInput,
    applyTypoSuggestion,
    validateBeforeSubmit,
    onSubmitSuccess,
} = useEmailFieldValidation({
    email,
    t,
});

const responseErrorMessage = ref('');

const displayMessage = computed<{ text: string; type: 'error' | 'suggestion' | 'nudge' } | null>(() => {
    if (responseErrorMessage.value) {
        return { text: responseErrorMessage.value, type: 'error' };
    }

    if (emailError.value) {
        return { text: emailError.value, type: 'error' };
    }

    if (suggestedEmail.value) {
        return {
            text: t('forms.emailTypoSuggestion', { email: suggestedEmail.value }),
            type: 'suggestion',
        };
    }

    if (roleNudgeMessage.value) {
        return { text: roleNudgeMessage.value, type: 'nudge' };
    }

    return null;
});

const eraseFormData = () => {
    formInput.value.email = '';
    responseErrorMessage.value = '';
    v$.$reset();
};

const modalStore = useModalStore();
const isOpen = ref(false);
let resolveFn: ((value: string | null) => void) | undefined;
let rejectFn: (() => void) | undefined;

function open(): Promise<string | null> {
    isOpen.value = true;
    modalStore.disableScroll();
    return new Promise<string | null>((resolve, reject) => {
        rejectFn = reject;
        resolveFn = resolve;
    });
}

const handleCloseModal = () => {
    isOpen.value = false;
    modalStore.enableScroll();
    resolveFn?.(null);
    eraseFormData();
};

const config = useRuntimeConfig();
const apiUrl = globalStore?.global?.UseMparticle ? '/api/subscribe-mparticle' : '/api/subscribe';
const subscribe = async (email: string) => {
    return await $fetch(apiUrl, {
        headers: { 'Content-type': 'application/json' },
        method: 'POST',
        body: {
            email,
            listId: config?.public.ITERABLE_LIST_ID,
            signupSource: blok.signupSource ?? '',
            culture: localeObj?.language,
        },
        onResponse({ response }) {
            if (response.ok) {
                isLoading.value = false;
                responseErrorMessage.value = '';
                resolveFn?.(blok.discountCode);
                eraseFormData();
                onSubmitSuccess();
                isOpen.value = false;
                modalStore.enableScroll();
            }
        },
        onResponseError({ response }) {
            if (response) {
                isLoading.value = false;
                responseErrorMessage.value = te('signupForm.emailSubscriptionInvalid')
                    ? t('signupForm.emailSubscriptionInvalid')
                    : t('forms.emailUnSubscriptionInvalid2');
            }
        },
    });
};

const onEmailInput = () => {
    onInput();
    responseErrorMessage.value = '';
};

const submitForm = async () => {
    responseErrorMessage.value = '';

    // Validate and check if can submit
    if (!validateBeforeSubmit()) {
        return;
    }

    isLoading.value = true;
    await subscribe(formInput.value.email);
};

onMounted(() => {
    useModalPrompt().registerOpen(open);
});
</script>

<style lang="postcss">
.discount-modal {
    .background {
        @apply bg-gray-900/30;
    }
    .margin-content {
        @apply px-8 shadow-none;
    }
    .modal-wrapper {
        @apply max-w-[700px] md:w-auto w-full md:h-auto mt-32;

        .modal-content {
            @apply rounded-xl relative;

            .modal-header,
            .modal-form,
            .modal-footer {
                @apply p-4;
            }

            .modal-header {
                @apply border-b border-neutral-200;

                .close {
                    @apply absolute top-0 right-0 text-4xl leading-[14px] cursor-pointer p-4 opacity-30 hover:opacity-50 transition-opacity;
                }
            }

            .modal-title {
                @apply text-lg font-bold;
            }

            .main-form {
                &__wrapper {
                    @apply flex flex-col md:flex-row gap-4 items-start;
                }
            }

            .modal-text {
                @apply mt-6 text-sm;
            }

            .input-group {
                @apply flex-grow w-full;

                .input-wrapper {
                    @apply relative w-full;
                }

                input {
                    @apply w-full px-2 py-2 border mb-0;
                    &::placeholder {
                        @apply text-gray-500;
                    }
                }
            }

            .modal-footer {
                @apply flex justify-end border-t border-neutral-200;

                .close {
                    @apply py-2 px-4 rounded text-primary border border-primary hover:bg-primary hover:text-white transition-colors;
                    &:disabled {
                        @apply opacity-30 hover:bg-white hover:text-primary;
                    }
                }
            }

            .button-group {
                @apply flex-shrink-0 md:mt-1 w-full md:w-auto md:max-w-[50%];
                button {
                    @apply py-2 px-4 rounded;
                    &[type='submit'] {
                        @apply bg-secondary/80 text-white text-base transition-all w-full;
                        &:hover:not(:disabled) {
                            @apply bg-secondary/100;
                        }
                        &:disabled {
                            @apply cursor-not-allowed;
                        }
                    }
                }
            }

            .success,
            .response-error {
                @apply mt-2;
                span {
                    @apply font-semibold;
                }
            }
            .success {
                @apply text-green-700;
            }
            .response-error {
                @apply text-error;
            }

            .error-message {
                @apply text-xs text-error mt-1 block w-full;

                &.suggestion-message {
                    @apply text-error;

                    span {
                        @apply underline cursor-pointer;
                    }
                }
            }
        }
    }
}
</style>
